<script setup>
import { ref, onMounted, computed, watch, nextTick } from 'vue'
import { useStore } from 'vuex'
import { HOST, API } from '@/utils/http'
import { bbox } from '@turf/turf'
import { debounce } from 'lodash'

import MapSource from '@/components/map/MapSource.vue'
import MapLayer from '@/components/map/MapLayer.vue'
import LayerRadarTrack from './LayerRadarTrack.vue'
import IconRadar from '@/assets/img/radar.svg'
// Import icons statically for initial loading
import IconArrow from '@/assets/img/arrow.svg'
import IconArrowOrange from '@/assets/img/arrow-orange.svg'
import IconArrowPrimary from '@/assets/img/arrow-primary.svg'

import {formatTime} from '@/utils/datetime'

const testBorderGeometry = {
  "type": "FeatureCollection",
  "features": [
    // {
    //   "type": "Feature",
    //   "geometry": {
    //     "type": "Polygon",
    //     "coordinates": [
    //       [
    //         [
    //           -85.464981319,
    //           9.83183177
    //         ],
    //         [
    //           -85.470927684,
    //           9.806014404
    //         ],
    //         [
    //           -85.45215947,
    //           9.801253536
    //         ],
    //         [
    //           -85.45215947,
    //           9.830550104
    //         ],
    //         [
    //           -85.464981319,
    //           9.83183177
    //         ]
    //       ]
    //     ]
    //   },
    //   "properties": {
    //   }
    // },
    // {
    //   "type": "Feature",
    //   "geometry": {
    //     "type": "Polygon",
    //     "coordinates": [
    //       [
    //         [
    //           -85.335975705,
    //           9.81608394
    //         ],
    //         [
    //           -85.339364695,
    //           9.809151285
    //         ],
    //         [
    //           -85.334753511,
    //           9.80622274
    //         ],
    //         [
    //           -85.332865167,
    //           9.813761334
    //         ],
    //         [
    //           -85.335975705,
    //           9.81608394
    //         ]
    //       ]
    //     ]
    //   },
    //   "properties": {
    //   }
    // },
  ]
}

const emit = defineEmits(['isLoaded'])
const props = defineProps({
  layer: {
    type: Object,
    required: true,
  },
  layerId: {
    type: String,
    required: true,
  },
  beforeId: {
    type: String,
    required: true,
  },
  groupId: {
    type: String,
    required: true,
  },
  options: {
    type: Object,
    required: false,
  },
  code: {
    type: String,
    required: true,
  },
  map: {
    type: Object,
    required: true,
  },
  isVisible: {
    type: Boolean,
    required: true,
  },
})

const store = useStore()
const settings = computed(() => store.getters['user/getUserSettings'])
const selectedRange = computed(() => store.getters['layers/getSelectedRange'])

const debouncedFetchResults = debounce(async () => {
  radar.value = await fetchResults()
  renderIncrement.value++
}, 300)

watch(selectedRange, async (newRange, oldRange) => {
  if (newRange !== oldRange) {
    debouncedFetchResults()
  }
}, { deep: true })

const fetchResults = async () => {
  store.commit('SET_IS_LOADING_DATA', true)
  try {
    const selectedRange = store.getters['layers/getSelectedRange'] || [];
    let url = `${HOST}/layers/${props.code}/radar/result`;

    if (selectedRange.length === 2) {
      // Ensure we have valid date strings in UTC format for the server
      const startDate = selectedRange[0];
      const endDate = selectedRange[1];

      url += `?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;
    }

    const response = await API({
      method: 'GET',
      url
    })

    return response.data
  } catch (e) {
    console.error('Error fetching radar data:', e)
    errorMessage.value = `Failed to load radar data: ${e.message || 'Unknown error'}`
    return []
  } finally {
    store.commit('SET_IS_LOADING_DATA', false)
  }
}

const fetchDates = async () => {
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/layers/${props.code}/radar/dates`
    })

    if (response.data && response.data.layers && Array.isArray(response.data.layers)) {
      const transformedLayers = response.data.layers.map(layer => {
        const processedDates = Array.isArray(layer.dates) ? layer.dates.map(dateItem => {
          if (dateItem.date && !dateItem.date.endsWith('Z') && !dateItem.date.includes('+')) {
            dateItem.date = dateItem.date + 'Z';
          }
          return dateItem;
        }) : [];

        return {
          id: layer.id || 'unknown',
          color: layer.color || 'blue',
          dates: processedDates
        };
      });

      return transformedLayers;
    } else if (response.data && response.data.dates) {
      const dates = Array.isArray(response.data.dates) ? response.data.dates : [];

      const processedDates = dates.map(dateItem => {
        if (dateItem.date && !dateItem.date.endsWith('Z') && !dateItem.date.includes('+')) {
          dateItem.date = dateItem.date + 'Z';
        }
        return dateItem;
      });

      return [{
        id: "radar",
        color: "blue",
        dates: processedDates
      }];
    }

    return [{
      id: "radar",
      dates: [],
      color: "blue"
    }];
  } catch (e) {
    console.error('Error fetching radar dates:', e)
    errorMessage.value = `Failed to load radar dates: ${e.message || 'Unknown error'}`
    return [{
      id: "radar",
      dates: [],
      color: "blue"
    }];
  }
}


const errorMessage = ref('')

const radar = ref(null)
const dates = ref(null)
const renderIncrement = ref(0)
const pollingRate = ref(60000)
const expandedCategories = ref({})
const activePopupTrackId = ref(null)

const trackPhotos = ref({})

const pollResults = async () => {
  radar.value = await fetchResults()

  renderIncrement.value++
}

const addMapImage = (imageId, imageSource) => {
  if (!props.map.hasImage(imageId)) {
    const image = new Image()
    image.onload = () => {
      if (!props.map.hasImage(imageId)) {
        try {
          // Set explicit width and height to ensure proper rendering in Firefox
          image.width = 140
          image.height = 140
          props.map.addImage(imageId, image)
        } catch (error) {
          console.error(`Error adding ${imageId}:`, error)
        }
      }
    }
    image.onerror = (e) => {
      console.error(`Failed to load ${imageId}:`, e)
    }
    image.src = imageSource
  }
}

const radarGeometry = computed(() => {
  if (radar.value) {
    return {
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [radar.value.longitude, radar.value.latitude]
        },
        properties: {}
      }]
    }
  }
  return null
})

const radarSweepGeometry = computed(() => {
  if (!radar.value || !radar.value.sweep) return null

  const sweep = radar.value.sweep
  const center = [radar.value.longitude, radar.value.latitude]

  const radius = 0.1
  const points = 64
  const coordinates = []

  for (let i = 0; i <= points; i++) {
    const angle = (i / points) * 360
    const radian = angle * Math.PI / 180
    coordinates.push([
      center[0] + Math.cos(radian) * radius,
      center[1] + Math.sin(radian) * radius
    ])
  }

  return {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [coordinates]
    },
    properties: {}
  }
})

const getStatusColor = (status) => {
  switch (status) {
    case 1:
      return 'text-green-500'
    case 0:
      return 'text-red-500'
    default:
      return 'text-gray-500'
  }
}

const handleRadarClick = () => {
  if (radar.value) {
    props.map.flyTo({
      center: [radar.value.longitude, radar.value.latitude],
      duration: 0
    })
  }
}

const groupedTracks = computed(() => {
  if (!radar.value || !radar.value.tracks) return {}

  const grouped = {}
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  const tracks = [...radar.value.tracks].sort((a, b) => new Date(b.last_update) - new Date(a.last_update))

  tracks.forEach(track => {
    const trackDate = new Date(track.last_update)
    trackDate.setHours(0, 0, 0, 0)

    let dateKey

    if (trackDate.getTime() === today.getTime()) {
      dateKey = 'Today'
    } else if (trackDate.getTime() === yesterday.getTime()) {
      dateKey = 'Yesterday'
    } else {
      // Format other dates as Month Day, Year
      dateKey = trackDate.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }

    if (!grouped[dateKey]) {
      grouped[dateKey] = []
    }

    grouped[dateKey].push(track)
  })

  return grouped
})

const toggleCategoryExpansion = (date) => {
  expandedCategories.value[date] = !expandedCategories.value[date]
}

const getVisibleTracks = (date, tracks) => {
  if (expandedCategories.value[date]) {
    return tracks
  }
  return tracks.slice(0, 8)
}

const handlePopupOpened = (trackId) => {
  activePopupTrackId.value = null

  nextTick(() => {
    activePopupTrackId.value = trackId
  })
}

const handlePopupClosed = () => {
  activePopupTrackId.value = null
}

onMounted(async () => {
  const now = new Date()
  const yesterday = new Date(now)
  yesterday.setHours(yesterday.getHours() - 24)

  const range = [
    yesterday.toISOString(),
    now.toISOString()
  ]

  if (!store.getters['layers/getSelectedRange'] || store.getters['layers/getSelectedRange'].length === 0) {
    store.commit('layers/SET_SELECTED_RANGE', range)
  }

  pollResults()
  setInterval(pollResults, pollingRate.value)

  if (props.map) {
    addMapImage('radar-icon', IconRadar)
    addMapImage('arrow-icon', IconArrow)
    addMapImage('arrow-orange-icon', IconArrowOrange)
    addMapImage('arrow-primary-icon', IconArrowPrimary)
  }

  dates.value = await fetchDates()

  if (dates.value) {
    try {
      if (Array.isArray(dates.value)) {
        store.commit('layers/ADD_DATES', dates.value)
      } else if (dates.value.id) {
        store.commit('layers/ADD_DATES', [dates.value])
      }
    } catch (error) {
      console.error('Error adding dates to store:', error)
    }
  }

  emit('isLoaded', true)
})


</script>

<template>
  <div v-if="radar && isVisible">
    <div>
      <div
        class="flex items-center justify-between pr-2"
        @click="handleRadarClick"
      >
        <div class="flex items-center">
          <span class="font-medium">{{ radar.name }}</span>
          <span :class="getStatusColor(radar.radar_status)">●</span>
        </div>
        <span class="text-gray-500">{{ radar.target_count }} targets</span>
      </div>

      <div class="mt-2 mr-2 mb-2">
        <div class="space-y-1">
          <div
            v-for="(tracks, date) in groupedTracks"
            :key="date"
            class="mb-6"
          >
            <div class="font-medium text-gray-700 mb-2 border-b pb-1 flex items-center">
              <span class="mr-2">📅</span>
              <span>{{ date }}</span>
              <span class="text-sm ml-2 text-gray-500">({{ tracks.length }} tracks)</span>
            </div>
            <div
              v-for="track in getVisibleTracks(date, tracks)"
              :key="track.id"
            >
              <LayerRadarTrack
                :track="track"
                :code="code"
                :expanded="false"
                :layerId="layerId"
                :map="map"
                :testBorderGeometry="testBorderGeometry"
                :isVisible="isVisible"
                :activePopupTrackId="activePopupTrackId"
                @popup-opened="handlePopupOpened"
                @popup-closed="handlePopupClosed"
              />
            </div>
            <div v-if="tracks.length > 8" class="text-center mt-2">
              <button
                @click="toggleCategoryExpansion(date)"
                class="text-blue-500 text-sm hover:underline"
              >
                {{ expandedCategories[date] ? 'Show less' : `Show ${tracks.length - 8} more tracks` }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div :key="renderIncrement">
      <MapSource
        v-if="radarGeometry"
        :map="map"
        type="geojson"
        :id="`${layerId}-radar-location-source`"
        :data="radarGeometry"
      >
        <MapLayer
          :map="map"
          :id="`${layerId}-radar-location-circle-layer`"
          type="circle"
          :source="`${layerId}-radar-location-source`"
          :options="{
            paint: {
              'circle-radius': 18,
              'circle-color': '#0059FF',
              'circle-opacity': 0.8,
            }
          }"
          @click="handleRadarClick"
        />
        <MapLayer
          :map="map"
          :id="`${layerId}-radar-location-layer`"
          type="symbol"
          :source="`${layerId}-radar-location-source`"
          :options="{
            layout: {
              'icon-image': 'radar-icon',
              'icon-size': 0.15,
              'icon-allow-overlap': true
            }
          }"
          @click="handleRadarClick"
        />
      </MapSource>

      <MapSource
        v-if="radarSweepGeometry"
        :map="map"
        type="geojson"
        :id="`${layerId}-sweep-source`"
        :data="radarSweepGeometry"
      >
        <MapLayer
          :map="map"
          :id="`${layerId}-sweep-layer`"
          type="fill"
          :source="`${layerId}-sweep-source`"
          :options="{
            paint: {
              'fill-color': '#0039A3',
              'fill-opacity': 0.1,
              'fill-outline-color': '#0039A3',
            }
          }"
        />
      </MapSource>

      <MapSource
        :map="map"
        type="geojson"
        :id="`${layerId}-test-border-source`"
        :data="testBorderGeometry"
      >
        <MapLayer
          :map="map"
          :id="`${layerId}-test-border-fill-layer`"
          type="fill"
          :source="`${layerId}-test-border-source`"
          :options="{
            paint: {
              'fill-color': '#FF0000',
              'fill-opacity': 0.01
            }
          }"
        />
        <MapLayer
          :map="map"
          :id="`${layerId}-test-border-line-layer`"
          type="line"
          :source="`${layerId}-test-border-source`"
          :options="{
            paint: {
              'line-color': '#FF0000',
              'line-width': 2,
              'line-opacity': 0.8
            }
          }"
        />
      </MapSource>
    </div>
  </div>
  <div v-else-if="!isVisible" class="text-sm text-gray-500">
  </div>
  <div v-else class="text-sm text-gray-500">
    Loading...
  </div>
  <div v-if="errorMessage && isVisible" class="text-sm text-red-500">
    {{ errorMessage }}
  </div>
</template>
